import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel } from "@/pages/admin/shared";

const input = "h-10 w-full rounded-lg border border-gold/40 bg-ivory px-3 text-sm outline-none focus:border-gold";

const CategoryRow = ({ cat, index, last, onMove, onSaved }) => {
  const [en, setEn] = useState(cat.name_en);
  const [bn, setBn] = useState(cat.name_bn);
  useEffect(() => { setEn(cat.name_en); setBn(cat.name_bn); }, [cat]);
  const dirty = en !== cat.name_en || bn !== cat.name_bn;

  const update = async (patch, msg) => {
    try { await api.put(`/admin/categories/${cat.id}`, patch); toast.success(msg); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Panel data-testid={`admin-category-row-${cat.key}`} className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex items-center gap-1 sm:flex-col">
        <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} data-testid={`category-up-${cat.key}`} className="rounded-lg p-1.5 text-maroon hover:bg-gold/20 disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
        <span className="font-display text-sm font-bold text-ink-muted">{index + 1}</span>
        <button type="button" disabled={last} onClick={() => onMove(index, 1)} data-testid={`category-down-${cat.key}`} className="rounded-lg p-1.5 text-maroon hover:bg-gold/20 disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
      </div>
      <div className="grid flex-1 gap-2 sm:grid-cols-2">
        <input value={en} onChange={(e) => setEn(e.target.value)} data-testid={`category-en-${cat.key}`} className={input} aria-label="English name" />
        <input value={bn} onChange={(e) => setBn(e.target.value)} data-testid={`category-bn-${cat.key}`} className={`${input} font-bengali`} aria-label="Bengali name" />
      </div>
      <div className="flex items-center gap-3">
        <button type="button" disabled={!dirty || !en.trim() || !bn.trim()} onClick={() => update({ name_en: en, name_bn: bn }, "Category renamed")} data-testid={`category-save-${cat.key}`} className="inline-flex items-center gap-1 rounded-full bg-forest px-3 py-2 text-xs font-semibold text-white disabled:opacity-30">
          <Save className="h-3.5 w-3.5" /> Save
        </button>
        <Switch checked={cat.active} onCheckedChange={(v) => update({ active: v }, v ? "Category shown" : "Category hidden")} data-testid={`category-active-${cat.key}`} />
      </div>
    </Panel>
  );
};

export default function Categories() {
  const [cats, setCats] = useState([]);
  const load = useCallback(() => api.get("/admin/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const move = async (i, dir) => {
    const ids = cats.map((c) => c.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    try { const { data } = await api.put("/admin/categories/order", { ids }); setCats(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-categories">
      <PageHeader title="Categories" sub="Bilingual names, order and visibility — changes are live instantly" />
      <div className="space-y-3">
        {cats.map((c, i) => <CategoryRow key={c.id} cat={c} index={i} last={i === cats.length - 1} onMove={move} onSaved={load} />)}
      </div>
    </div>
  );
}
